/**
 * Utility functions for generating and managing activity actions in the Ads Manager
 */

import type { ApolloCache } from '@apollo/client'

// Type definitions for activity actions and related objects
export interface User {
    firstName?: string
    lastName?: string
    id: string
    email?: string
}

export interface ActivityAction {
    __typename: 'ActivityAction'
    event_type: string
    origin: 'system' | 'user' | 'meta'
    event_time: string
    actor_name: string
    object_name: string
    extra_data: string
}

export interface StatusMapping {
    code: number
    text: 'Active' | 'Inactive'
}

export interface RunStatusExtraData {
    run_status: {
        old_value: number
        new_value: number
    }
    old_value: string
    new_value: string
    type: 'run_status'
}

export interface AdStatusExtraData extends RunStatusExtraData {
    campaign_id: null
}

export interface PaymentAmount {
    type: 'payment_amount'
    currency: string
    old_value?: number
    new_value?: number
    additional_type: 'status_string'
    additional_value: string
}

export interface BudgetExtraData {
    old_value: PaymentAmount
    new_value: PaymentAmount
    type: 'composite_data'
}

export interface CreateActivityActionParams {
    eventType: string
    objectName: string
    extraData: string
    user: User
}

export interface CreateCampaignStatusActionParams {
    campaignName: string
    oldStatus: boolean
    newStatus: boolean
    user: User
}

export interface CreateAdSetStatusActionParams {
    adsetName: string
    oldStatus: boolean
    newStatus: boolean
    user: User
}

export interface CreateAdStatusActionParams {
    adName: string
    oldStatus: boolean
    newStatus: boolean
    user: User
}

export interface CreateAdSetBudgetActionParams {
    adsetName: string
    oldBudget: number
    newBudget: number
    currency?: string
    user: User
}

/**
 * Creates a base ActivityAction object with common fields
 */
export function createActivityAction({
    eventType,
    objectName,
    extraData,
    user,
}: CreateActivityActionParams): ActivityAction {
    const actorName = user && user.firstName && user.lastName ? `${user.firstName} ${user.lastName}` : 'Unknown User'

    return {
        __typename: 'ActivityAction',
        event_type: eventType,
        origin: 'system',
        event_time: new Date().toISOString(),
        actor_name: actorName,
        object_name: objectName,
        extra_data: extraData,
    }
}

/**
 * Creates an activity action for campaign status changes
 */
export function createCampaignStatusAction({
    campaignName,
    oldStatus,
    newStatus,
    user,
}: CreateCampaignStatusActionParams): ActivityAction {
    // Map boolean status to Meta status codes and strings
    const statusMapping: Record<string, StatusMapping> = {
        true: { code: 1, text: 'Active' },
        false: { code: 15, text: 'Inactive' },
    }

    const oldMapping = statusMapping[String(oldStatus)]
    const newMapping = statusMapping[String(newStatus)]

    const extraDataObj: RunStatusExtraData = {
        run_status: {
            old_value: oldMapping.code,
            new_value: newMapping.code,
        },
        old_value: oldMapping.text,
        new_value: newMapping.text,
        type: 'run_status',
    }

    const extraData = JSON.stringify(extraDataObj)

    return createActivityAction({
        eventType: 'update_campaign_run_status',
        objectName: campaignName,
        extraData,
        user,
    })
}

/**
 * Creates an activity action for adset status changes
 */
export function createAdSetStatusAction({
    adsetName,
    oldStatus,
    newStatus,
    user,
}: CreateAdSetStatusActionParams): ActivityAction {
    // Map boolean status to Meta status codes and strings
    const statusMapping: Record<string, StatusMapping> = {
        true: { code: 1, text: 'Active' },
        false: { code: 7, text: 'Inactive' },
    }

    const oldMapping = statusMapping[String(oldStatus)]
    const newMapping = statusMapping[String(newStatus)]

    const extraDataObj: RunStatusExtraData = {
        old_value: oldMapping.text,
        new_value: newMapping.text,
        run_status: {
            old_value: oldMapping.code,
            new_value: newMapping.code,
        },
        type: 'run_status',
    }

    const extraData = JSON.stringify(extraDataObj)

    return createActivityAction({
        eventType: 'update_ad_set_run_status',
        objectName: adsetName,
        extraData,
        user,
    })
}

/**
 * Creates an activity action for ad status changes
 */
export function createAdStatusAction({
    adName,
    oldStatus,
    newStatus,
    user,
}: CreateAdStatusActionParams): ActivityAction {
    // Map boolean status to Meta status codes and strings
    const statusMapping: Record<string, StatusMapping> = {
        true: { code: 1, text: 'Active' },
        false: { code: 8, text: 'Inactive' },
    }

    const oldMapping = statusMapping[String(oldStatus)]
    const newMapping = statusMapping[String(newStatus)]

    const extraDataObj: AdStatusExtraData = {
        old_value: oldMapping.text,
        new_value: newMapping.text,
        campaign_id: null, // Will be populated when we have campaign context
        run_status: {
            old_value: oldMapping.code,
            new_value: newMapping.code,
        },
        type: 'run_status',
    }

    const extraData = JSON.stringify(extraDataObj)

    return createActivityAction({
        eventType: 'update_ad_run_status',
        objectName: adName,
        extraData,
        user,
    })
}

/**
 * Creates an activity action for adset budget changes
 */
export function createAdSetBudgetAction({
    adsetName,
    oldBudget,
    newBudget,
    currency = 'AUD',
    user,
}: CreateAdSetBudgetActionParams): ActivityAction {
    const extraDataObj: BudgetExtraData = {
        old_value: {
            type: 'payment_amount',
            currency,
            old_value: oldBudget,
            additional_type: 'status_string',
            additional_value: '',
        },
        new_value: {
            type: 'payment_amount',
            currency,
            new_value: newBudget,
            additional_type: 'status_string',
            additional_value: 'Per day',
        },
        type: 'composite_data',
    }

    const extraData = JSON.stringify(extraDataObj)

    return createActivityAction({
        eventType: 'update_ad_set_budget',
        objectName: adsetName,
        extraData,
        user,
    })
}

/**
 * Adds an activity action to the Apollo Client cache for a specific entity
 */
export function addActivityActionToCache(
    cache: ApolloCache<any>,
    entityIdentifier: string,
    activityAction: ActivityAction,
): void {
    try {
        cache.modify({
            id: entityIdentifier,
            fields: {
                latest_actions(existingActions: readonly any[] = []): any[] {
                    // Prepend new action to make it appear first (newest first)
                    return [activityAction, ...existingActions]
                },
            },
        })
    } catch (error) {
        console.warn('Failed to add activity action to cache:', error)
    }
}
